// src/pages/rules/RulesTable.jsx
//
// The "Existing Rules" list: search/scope/type toolbar, the DataTable
// itself (columns defined here since they're presentation, not state --
// see useRulesConfig.js for that), and pagination. Split out of
// RulesConfigPage.jsx.

import { useMemo } from "react";
import PropTypes from "prop-types";
import { ListChecks, Pencil, Plus, Trash2 } from "lucide-react";
import {
  Button,
  DataTable,
  EmptyState,
  Input,
  Pagination,
  Select,
  SkeletonTable,
  StatusBadge,
  SurfaceCard,
} from "../../shared/ui";
import { SCOPE_OPTIONS } from "./ruleOptions";

export default function RulesTable({
  rules,
  loading,
  hasActiveFilters,
  canManageRule,
  canManageGlobal,
  canManageOrgWide,
  manageableAccountIds,
  accountNameById,
  onEdit,
  onDeleteRequest,
  openAddForm,
  searchInput,
  onSearchChange,
  scopeFilter,
  onScopeFilterChange,
  filterType,
  onFilterTypeChange,
  filterOptions,
  page,
  totalPages,
  onPageChange,
}) {
  const ruleColumns = useMemo(
    () => [
      {
        id: "scope",
        header: "Scope",
        accessorFn: (r) =>
          r.scope === "global"
            ? "Global"
            : r.scope === "org"
            ? "Org-wide"
            : accountNameById.get(r.account_id) || `Account #${r.account_id}`,
        width: "140px",
      },
      {
        id: "contract_type",
        header: "Type",
        accessorFn: (r) => r.contract_type || "GLOBAL",
        width: "110px",
      },
      { id: "dimension", header: "Dimension", accessorKey: "dimension" },
      { id: "name", header: "Name", accessorKey: "name" },
      { id: "category", header: "Category", accessorKey: "category" },
      {
        id: "severity",
        header: "Severity",
        accessorKey: "severity",
        cell: (info) => <StatusBadge map="priority" status={info.getValue()} />,
      },
      { id: "weight", header: "Weight", accessorKey: "weight", align: "right" },
      {
        id: "is_mandatory",
        header: "Mandatory",
        accessorFn: (r) => (r.is_mandatory ? "Yes" : "No"),
        align: "center",
      },
      {
        id: "active",
        header: "Active",
        accessorFn: (r) => (r.active ? "Yes" : "No"),
        align: "center",
      },
      {
        id: "actions",
        header: "Actions",
        sortAllowed: false,
        align: "right",
        cell: (info) => {
          const rule = info.row.original;
          if (!canManageRule(rule)) {
            return <span className="text-xs text-muted">Read-only</span>;
          }
          return (
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="!px-2"
                title="Edit rule"
                aria-label="Edit rule"
                onClick={() => onEdit(rule)}
              >
                <Pencil size={14} />
              </Button>
              <Button
                size="sm"
                variant="danger"
                className="!px-2"
                title="Delete rule"
                aria-label="Delete rule"
                onClick={() => onDeleteRequest(rule)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          );
        },
      },
    ],
    // onEdit/onDeleteRequest are stable across renders in practice (passed
    // down once per RulesConfigPage instance, no external deps that change
    // their behavior) -- the permission/lookup values below are the only
    // deps that actually change what a row renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManageGlobal, canManageOrgWide, manageableAccountIds, accountNameById]
  );

  return (
    <>
      <SurfaceCard padding="p-0" className="overflow-hidden">
        {loading && rules.length === 0 ? (
          <SkeletonTable rows={5} columns={10} />
        ) : rules.length === 0 && !hasActiveFilters ? (
          <EmptyState
            icon={ListChecks}
            title="No rules configured yet"
            description="Add the first rule to get started."
            action={
              <Button variant="secondary" onClick={openAddForm}>
                <Plus size={15} /> Add Rule
              </Button>
            }
          />
        ) : (
          <DataTable
            title="Existing Rules"
            table={{ columns: ruleColumns, rows: rules }}
            entriesPerPage={false}
            emptyMessage="No rules found for this filter."
            toolbarContent={
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <div className="w-52">
                  <Input
                    type="search"
                    placeholder="Search name or description…"
                    value={searchInput}
                    onChange={onSearchChange}
                  />
                </div>
                <div className="w-40">
                  <Select options={SCOPE_OPTIONS} value={scopeFilter} onChange={onScopeFilterChange} />
                </div>
                <div className="w-40">
                  <Select options={filterOptions} value={filterType} onChange={onFilterTypeChange} />
                </div>
                {loading && <span className="text-xs text-muted">Loading…</span>}
              </div>
            }
          />
        )}
      </SurfaceCard>

      <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
    </>
  );
}

RulesTable.propTypes = {
  rules: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  hasActiveFilters: PropTypes.bool.isRequired,
  canManageRule: PropTypes.func.isRequired,
  canManageGlobal: PropTypes.bool.isRequired,
  canManageOrgWide: PropTypes.bool.isRequired,
  manageableAccountIds: PropTypes.array.isRequired,
  accountNameById: PropTypes.instanceOf(Map).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDeleteRequest: PropTypes.func.isRequired,
  openAddForm: PropTypes.func.isRequired,
  searchInput: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  scopeFilter: PropTypes.string.isRequired,
  onScopeFilterChange: PropTypes.func.isRequired,
  filterType: PropTypes.string.isRequired,
  onFilterTypeChange: PropTypes.func.isRequired,
  filterOptions: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};
